(() => {
  const C = window.JoffzProductConfigurator;
  const name = window.__JPC_ELEMENT_NAME || 'joffz-product-configurator';
  if (!C) return;

  if (!C.prototype.__jpcV59LocaleSafeVariants) {
    C.prototype.__jpcV59LocaleSafeVariants = true;

    const baseApplyVariant = C.prototype.applyVariant;
    const baseUpdatePriceAndAvailability = C.prototype.updatePriceAndAvailability;
    const baseRenderUnavailable = C.prototype.renderUnavailable;

    const normalizeText = (value) =>
      String(value || '')
        .trim()
        .toLocaleLowerCase('en')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ß/g, 'ss')
        .replace(/ı/g, 'i')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ');

    const hasAny = (text, values) => values.some((value) => text.includes(value));

    // V65: identify material before frame colour; a floating canvas is never paper.
    const semanticFromText = (value) => {
      const canonical = String(value || '').trim();
      if (/^(paper:(print-only|black|oak|white)|canvas:(unframed|black|oak|white))$/.test(canonical)) return canonical;
      const text = normalizeText(value);
      if (!text || text === 'framed' || text === 'framed print or canvas') return null;

      const black = hasAny(text, ['black', 'schwarz', 'siyah', 'noir', 'negro', 'nero', 'nera']);
      const oak = hasAny(text, ['oak', 'eiche', 'eichen', 'mese', 'chene', 'roble', 'quercia', 'rovere', 'natural wood']);
      const white = hasAny(text, ['white', 'weiss', 'beyaz', 'blanc', 'blanco', 'bianco', 'bianca']);
      const colour = black ? 'black' : oak ? 'oak' : white ? 'white' : null;
      const canvas = hasAny(text, [
        'canvas', 'leinwand', 'tuval', 'kanvas', 'toile', 'lienzo',
        'floating', 'schattenfugen', 'caisse americaine', 'flotante', 'flottante', 'yuzer'
      ]) || /\btela\b/.test(text);
      if (canvas) return `canvas:${colour || 'unframed'}`;
      if (colour) return `paper:${colour}`;

      const paper = hasAny(text, [
        'poster', 'print', 'druck', 'baski', 'affiche',
        'impresion', 'stampa', 'paper', 'papier', 'kagit', 'lamina', 'papel', 'carta'
      ]);
      return paper ? 'paper:print-only' : null;
    };

    const semanticFromSku = (sku) => {
      const value = String(sku || '').toUpperCase();
      if (value.includes('-FBLK-')) return 'canvas:black';
      if (value.includes('-FWHT-')) return 'canvas:white';
      if (value.includes('-FOAK-')) return 'canvas:oak';
      if (value.includes('-PST-')) return 'paper:print-only';
      if (value.includes('-CAN-')) return 'canvas:unframed';
      if (value.includes('-OAK-')) return 'paper:oak';
      if (value.includes('-WHT-') || value.includes('-WFP-')) return 'paper:white';
      if (value.includes('-BLK-') || value.includes('-BFP-')) return 'paper:black';
      return null;
    };

    const labelFor = (root, input) => {
      if (!root || !input?.id) return null;
      return root.querySelector(`label[for="${input.id}"]`);
    };

    C.prototype.detectOptionKeys = function detectOptionKeysV59() {
      const keySet = new Set();
      this.variants.forEach((variant) => {
        Object.keys(variant?.options || {}).forEach((key) => keySet.add(key));
      });
      this.optionKeys = [...keySet];

      const scoreSizeKey = (key) => this.variants.reduce((score, variant) => {
        const value = variant?.options?.[key];
        if (!value) return score;
        try {
          const metric = this.parsePhysicalSize(value);
          return score + (Number.isFinite(metric?.longEdge) ? 1 : 0);
        } catch (error) {
          return score;
        }
      }, 0);

      const scoreFinishKey = (key) => this.variants.reduce((score, variant) => {
        return score + (semanticFromText(variant?.options?.[key]) ? 1 : 0);
      }, 0);

      this.sizeOptionKey = [...this.optionKeys]
        .map((key) => ({ key, score: scoreSizeKey(key) }))
        .sort((a, b) => b.score - a.score)[0]?.key || null;

      this.finishOptionKey = [...this.optionKeys]
        .filter((key) => key !== this.sizeOptionKey)
        .map((key) => ({
          key,
          score: scoreFinishKey(key),
          distinct: new Set(this.variants.map((variant) => normalizeText(variant.options?.[key]))).size,
        }))
        .sort((a, b) => b.score - a.score || b.distinct - a.distinct)[0]?.key ||
        this.optionKeys.find((key) => key !== this.sizeOptionKey) ||
        this.optionKeys[0] || null;

      if (!this.finishOptionKey || !this.sizeOptionKey) {
        throw new Error('Finish and size options could not be identified.');
      }
    };

    C.prototype.getVariantSemanticV58 = function getVariantSemanticV65(variant) {
      if (!variant) return null;
      const explicit = String(variant.finishSemantic || '').trim();
      if (explicit) return explicit;
      let semantic = semanticFromText(variant.options?.[this.finishOptionKey]) || semanticFromSku(variant.sku);
      if (!semantic) return null;

      // Legacy three-option products keep their explicit product material.
      const auxiliary = Object.entries(variant.options || {})
        .filter(([key]) => key !== this.finishOptionKey && key !== this.sizeOptionKey)
        .map(([, value]) => normalizeText(value));
      const canvas = auxiliary.some((value) => ['canvas', 'stretched canvas', 'leinwand', 'toile', 'lienzo', 'tela', 'kanvas', 'tuval'].includes(value));
      const paper = auxiliary.some((value) => ['poster', 'paper', 'print', 'papier', 'papel', 'carta', 'kagit'].includes(value));
      const frame = semantic.split(':')[1];
      if (canvas && !paper) semantic = `canvas:${frame === 'print-only' ? 'unframed' : frame}`;
      if (paper && !canvas) semantic = `paper:${frame === 'unframed' ? 'print-only' : frame}`;
      return semantic;
    };

    C.prototype.getVariantSizeV58 = function getVariantSizeV59(variant) {
      if (!variant) return '';
      if (variant.sizeValue) return variant.sizeValue;

      const direct = variant.options?.[this.sizeOptionKey];
      if (direct) {
        try {
          if (Number.isFinite(this.parsePhysicalSize(direct)?.longEdge)) return direct;
        } catch (error) {}
      }

      for (const value of Object.values(variant.options || {})) {
        try {
          if (Number.isFinite(this.parsePhysicalSize(value)?.longEdge)) return value;
        } catch (error) {}
      }
      return direct || '';
    };

    C.prototype.canonicalFinish = function canonicalFinishV59(value) {
      return semanticFromText(value);
    };

    C.prototype.canonicalVariantFinish = function canonicalVariantFinishV59(variant) {
      return this.getVariantSemanticV58(variant);
    };

    C.prototype.semanticForFrameInput = function semanticForFrameInputV59(input) {
      if (!input) return null;
      const material = input.dataset.materialScope;
      const frame = input.value;
      if (material === 'paper') {
        if (frame === 'print-only') return 'paper:print-only';
        if (['black', 'oak', 'white'].includes(frame)) return `paper:${frame}`;
      }
      if (material === 'canvas') {
        if (frame === 'unframed') return 'canvas:unframed';
        if (['black', 'oak', 'white'].includes(frame)) return `canvas:${frame}`;
      }
      return semanticFromText(input.dataset.nativeFinish);
    };

    C.prototype.rebuildSizeControlsV58 = function rebuildSizeControlsV59() {
      const grid = this.querySelector('.jpc-size-grid');
      if (!grid || !this.sizeValues?.length) return;

      const previousInputs = [...grid.querySelectorAll('[data-jpc-size]')];
      const groupName = previousInputs[0]?.name || `jpc-size-${this.dataset.sectionId || 'product'}`;
      const sectionKey = this.dataset.sectionId || 'product';
      const fragment = document.createDocumentFragment();

      this.sizeValues.forEach((size, index) => {
        const input = document.createElement('input');
        input.className = 'jpc-visually-hidden';
        input.type = 'radio';
        input.id = `JpcSizeRuntime-${sectionKey}-${index + 1}`;
        input.name = groupName;
        input.value = size;
        input.setAttribute('data-jpc-size', '');

        const label = document.createElement('label');
        label.className = 'jpc-size-pill';
        label.htmlFor = input.id;
        label.textContent = size;

        fragment.append(input, label);
      });

      grid.replaceChildren(fragment);
      this.sizeInputs = [...grid.querySelectorAll('[data-jpc-size]')];

      const dialogList = this.querySelector('.jpc-size-dialog-list');
      if (dialogList) {
        const dialogFragment = document.createDocumentFragment();
        this.sizeValues.forEach((size) => {
          const span = document.createElement('span');
          span.textContent = size;
          dialogFragment.appendChild(span);
        });
        dialogList.replaceChildren(dialogFragment);
      }
    };

    C.prototype.prepareIndexes = function prepareIndexesV59() {
      this.variantById = new Map();
      this.variantByCombination = new Map();
      this.variantBySemanticCombination = new Map();
      this.finishValues = new Map();
      this.sizeValues = [];

      this.variants.forEach((variant) => {
        this.variantById.set(String(variant.id), variant);
        const finish = variant.options?.[this.finishOptionKey];
        const size = this.getVariantSizeV58(variant);
        if (finish && size) {
          this.variantByCombination.set(this.combinationKey(finish, size), variant);
          this.finishValues.set(this.normalize(finish), finish);
          if (!this.sizeValues.includes(size)) this.sizeValues.push(size);
        }

        const semantic = this.getVariantSemanticV58(variant);
        if (semantic && size) {
          const key = `${semantic}::${this.normalize(size)}`;
          const existing = this.variantBySemanticCombination.get(key);
          if (!existing || (!existing.available && variant.available)) {
            this.variantBySemanticCombination.set(key, variant);
          }
        }
      });

      this.sizeMetrics = new Map(
        this.sizeValues.map((size) => [size, this.parsePhysicalSize(size)])
      );
      this.rebuildSizeControlsV58();
    };

    C.prototype.findVariant = function findVariantV59(finish, size) {
      if (!finish || !size) return null;

      const requestedSemantic = semanticFromText(finish);
      const direct = this.variantByCombination?.get(this.combinationKey(finish, size));
      if (direct?.available && (!requestedSemantic || this.getVariantSemanticV58(direct) === requestedSemantic)) return direct;

      const semantic = requestedSemantic || finish;
      const semanticVariant = this.variantBySemanticCombination?.get(
        `${semantic}::${this.normalize(size)}`
      );
      if (semanticVariant?.available) return semanticVariant;

      return null;
    };

    C.prototype.findClosestExistingSize = function findClosestExistingSizeV59(
      finish,
      desiredSize,
      { preferAvailable = true } = {}
    ) {
      const semantic = semanticFromText(finish) || finish;
      let candidates = this.variants.filter(
        (variant) => this.getVariantSemanticV58(variant) === semantic
      );

      if (!candidates.length && !semanticFromText(finish)) {
        candidates = this.variants.filter(
          (variant) => this.normalize(variant.options?.[this.finishOptionKey]) === this.normalize(finish)
        );
      }

      if (preferAvailable || candidates.some((variant) => variant.available)) {
        candidates = candidates.filter((variant) => variant.available);
      }
      if (!candidates.length) return null;

      const desiredMetric = this.sizeMetrics.get(desiredSize)?.longEdge;
      if (!Number.isFinite(desiredMetric)) return this.getVariantSizeV58(candidates[0]);

      return candidates
        .map((variant) => {
          const size = this.getVariantSizeV58(variant);
          const metric = this.sizeMetrics.get(size)?.longEdge;
          return {
            size,
            distance: Number.isFinite(metric)
              ? Math.abs(metric - desiredMetric)
              : Number.POSITIVE_INFINITY,
          };
        })
        .sort((a, b) => a.distance - b.distance)[0]?.size || null;
    };

    C.prototype.configureStaticAvailability = function configureStaticAvailabilityV59() {
      this.frameInputs.forEach((input) => {
        const permanent = input.hasAttribute('disabled') && Boolean(input.dataset.disabledReason);
        input.dataset.permanentlyDisabled = permanent ? 'true' : 'false';
        const semantic = this.semanticForFrameInput(input);
        const exists = !permanent && Boolean(semantic) && this.variants.some(
          (variant) => variant.available && this.getVariantSemanticV58(variant) === semantic
        );
        input.disabled = !exists;
        input.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');
        const label = labelFor(this, input);
        if (label) label.hidden = !exists;
      });

      this.materialInputs.forEach((input) => {
        const scopedFrames = this.frameInputs.filter(
          (frameInput) => frameInput.dataset.materialScope === input.value
        );
        const enabled = scopedFrames.some((frameInput) => !frameInput.disabled);
        input.disabled = !enabled;
        input.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        const label = labelFor(this, input);
        if (label) label.hidden = !enabled;
      });
    };

    C.prototype.updateFrameAvailability = function updateFrameAvailabilityV59(material) {
      this.frameInputs.forEach((input) => {
        if (input.dataset.materialScope !== material) return;
        const permanent = input.dataset.permanentlyDisabled === 'true';
        const semantic = this.semanticForFrameInput(input);
        const exists = !permanent && Boolean(semantic) && this.variants.some(
          (variant) => variant.available && this.getVariantSemanticV58(variant) === semantic
        );
        input.disabled = !exists;
        input.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');
        const label = labelFor(this, input);
        if (label) label.hidden = !exists;
      });
    };

    C.prototype.updateSizeAvailability = function updateSizeAvailabilityV59(finish) {
      this.sizeInputs.forEach((input) => {
        const variant = this.findVariant(finish, input.value);
        const available = Boolean(variant?.available);
        input.disabled = !available;
        input.setAttribute('aria-disabled', available ? 'false' : 'true');
        const label = labelFor(this, input);
        if (label) label.hidden = !available;
        if (!available && input.checked) input.checked = false;
      });
    };

    C.prototype.stateFromVariant = function stateFromVariantV59(variant) {
      const semantic = this.getVariantSemanticV58(variant) || 'paper:print-only';
      const [material, frame] = semantic.split(':');
      return {
        material: material || 'paper',
        frame: frame || (material === 'canvas' ? 'unframed' : 'print-only'),
        mount: 'no',
        size: this.getVariantSizeV58(variant) || this.sizeValues[0] || '',
      };
    };

    C.prototype.applyVariant = function applyVariantV59(variant, options = {}) {
      let resolved = variant;
      if (!resolved?.available) {
        const semantic = this.getVariantSemanticV58(resolved);
        const size = this.getVariantSizeV58(resolved);
        resolved = this.variants.find(
          (candidate) =>
            candidate.available &&
            this.getVariantSemanticV58(candidate) === semantic &&
            this.normalize(this.getVariantSizeV58(candidate)) === this.normalize(size)
        ) || this.variants.find(
          (candidate) => candidate.available && this.getVariantSemanticV58(candidate) === semantic
        ) || this.variants.find((candidate) => candidate.available) || null;
      }

      // Reuse the existing selectable-variant policy without enabling disabled formats.
      if (resolved && typeof this.coerceVariantToSelectable === 'function') {
        resolved = this.coerceVariantToSelectable(resolved);
      }
      if (resolved && typeof this.isSemanticSelectable === 'function' &&
          !this.isSemanticSelectable(this.getVariantSemanticV58(resolved))) resolved = null;

      if (!resolved) {
        if (typeof baseRenderUnavailable === 'function') {
          return baseRenderUnavailable.call(this, this.labels.unavailable || this.dataset.messageUnavailable || '');
        }
        return;
      }

      return baseApplyVariant.call(this, resolved, options);
    };

    C.prototype.updatePriceAndAvailability = function updatePriceAndAvailabilityV59(variant) {
      if (typeof baseUpdatePriceAndAvailability === 'function') {
        baseUpdatePriceAndAvailability.call(this, variant);
      }
      if (!variant?.available) {
        this.soldOutMessage?.classList.add('hidden');
      }
    };
  }

  if (!customElements.get(name)) customElements.define(name, C);
})();
